import { Button, cn, Input, Select, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { SkeletonRows } from '../_ops/SkeletonRows.js';
import type { CustomerDetailResponse, ImportCandidate } from './contractsMirror.js';
import { FIELD_LABEL } from './labelStyles.js';
import {
  clientQueryKey,
  useClientQuery,
  useClientsQuery,
  useMergeClientMutation,
} from './useClientsQuery.js';

/**
 * The merge screen (`/clients/merge`). It cleans up suspected-duplicate clients
 * found by the "Import from documents" scan.
 *
 * Two ways in, both rendering the same per-group UI:
 *  - Pairs from the import result, handed over via router STATE (ephemeral — a
 *    bookmarkable merge URL would go stale the moment the duplicates were merged).
 *  - No state → pick two clients by search, from the loaded directory.
 *
 * Each group: members side by side, a "Keep this one" choice, and a Merge button
 * that POSTs `/customers/:loser/merge { into: winner }` once per loser, then
 * removes the group from the list. A failed merge keeps the group and says so —
 * nothing is removed that did not actually merge.
 *
 * Merge never fires on the first click. It opens a confirm that names which
 * record goes into which and says it cannot be undone, because the loser is
 * deleted and there is no restore.
 *
 * The two member cards share ONE row grid (CSS `subgrid`), so name, email,
 * phone, address, document count and the keep choice each sit on the same line
 * in both columns no matter how long one side's address runs — and the choice
 * is anchored to the shared bottom row. Alignment here never depends on content
 * length: this is the screen where a ragged column would make a reader compare
 * the wrong two values and delete the wrong record.
 */

/** The feature's panel shape. `p-4` first so a 375px phone is not 48px of padding. */
const SECTION = 'rounded-md border border-border bg-card p-4 sm:p-6';
/**
 * Native radio in the QELM accent — the house treatment (RoleCards, BillingPage,
 * WeekGrid, _ops/DataTable). Without `accent-primary` the browser paints the checked
 * state in the OS accent, so choosing a keeper turned the card orange and the radio
 * BLUE in the same click: two accents firing on one decision.
 *
 * No radio in this file is ever `disabled`: Chrome paints a disabled unchecked radio
 * as a FILLED grey dot, and on the dark theme the picker's two "Keep client A/B"
 * radios read as ALREADY SELECTED before either side had been picked — on the one
 * screen where the choice decides which record gets deleted. `opacity-50` dims that
 * dot without un-filling it, so the choice is withheld until it exists instead.
 */
const RADIO =
  'size-4 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export interface MergeScreenProps {
  candidates: ImportCandidate[];
}

export function MergeScreen({ candidates }: MergeScreenProps) {
  const { t } = useTranslation();
  // The working list: candidates minus the groups already merged away.
  const [remaining, setRemaining] = useState<ImportCandidate[]>(candidates);

  // Arriving WITH candidates and clearing them all is a finished job, not an empty
  // one. Without this the last Merge silently swapped the screen for a "Pick two
  // clients" form — the reader finished the work and was handed the form again,
  // with nothing saying it worked and no way back to the list.
  const finished = candidates.length > 0 && remaining.length === 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('clients:merge.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {t('clients:merge.subtitle')}
          </p>
        </div>
        {/* Hidden only when the finished panel below carries the same label as its
            primary CTA — one view, one "Back to clients". */}
        {finished ? null : (
          <Button asChild variant="secondary">
            <Link to="/clients">{t('clients:merge.backToClients')}</Link>
          </Button>
        )}
      </header>

      {remaining.length > 0 ? (
        <div className="space-y-4">
          {remaining.map((group, index) => (
            <CandidateGroup
              key={group.ids.join('-')}
              group={group}
              groupIndex={index}
              onDone={() =>
                setRemaining((prev) => prev.filter((g) => g.ids.join('-') !== group.ids.join('-')))
              }
            />
          ))}
        </div>
      ) : finished ? (
        <MergeFinished />
      ) : (
        <ManualPicker />
      )}
    </div>
  );
}

/** Every group the scan found has been dealt with. Say so, and point back to the list. */
function MergeFinished() {
  const { t } = useTranslation();
  return (
    <section className={`${SECTION} max-w-2xl`} role="status">
      <h2 className="font-display font-semibold text-foreground text-lg">
        {t('clients:merge.finishedHeading')}
      </h2>
      <p className="mt-2 text-muted-foreground text-sm">{t('clients:merge.finishedBody')}</p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/clients">{t('clients:merge.backToClients')}</Link>
        </Button>
      </div>
    </section>
  );
}

/** One suspected-duplicate group: members side by side + a keep choice + Merge. */
function CandidateGroup({
  group,
  groupIndex,
  onDone,
}: {
  group: ImportCandidate;
  groupIndex: number;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const merge = useMergeClientMutation();
  const [keeperId, setKeeperId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(false);

  const losers = group.ids.filter((id) => id !== keeperId);

  // The same name the member card shows: the loaded record first, then the scan's name.
  const nameOf = (id: number) =>
    queryClient.getQueryData<CustomerDetailResponse>(clientQueryKey(id))?.customer?.name ||
    group.names[group.ids.indexOf(id)] ||
    t('clients:table.unnamedClient', { id });

  async function runMerge() {
    if (keeperId === null || losers.length === 0) return;
    setError(false);
    try {
      // One POST per loser; every one must succeed before the group goes away.
      await Promise.all(losers.map((loserId) => merge.mutateAsync({ loserId, intoId: keeperId })));
      onDone();
    } catch {
      setError(true);
    }
  }

  return (
    <section className={SECTION}>
      <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
        {t('clients:merge.groupHeading', { index: groupIndex + 1 })}
      </h2>

      <fieldset>
        {/* Visible, not sr-only: it is the instruction, and Merge stays disabled
            until it is followed. A disabled button with nothing saying why is a
            dead control. */}
        <legend className="mb-3 text-muted-foreground text-sm">
          {t('clients:merge.choiceLegend')}
        </legend>
        {/* Six shared rows — name, the four fields, the choice — so the two records
            line up line for line however long one side's address runs, and the
            radio sits on the same baseline in both columns. A ragged comparison
            grid is the loudest failure a merge screen can have. */}
        <div className="grid gap-x-4 gap-y-4 md:grid-cols-2 md:grid-rows-[auto_auto_auto_auto_auto_auto] md:gap-y-2">
          {group.ids.map((id, memberIndex) => (
            <MergeMember
              key={id}
              id={id}
              fallbackName={group.names[memberIndex] ?? ''}
              keeperId={keeperId}
              groupName={`keep-${groupIndex}`}
              onChoose={() => setKeeperId(id)}
            />
          ))}
        </div>
      </fieldset>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={keeperId === null || merge.isPending}
        >
          {merge.isPending ? t('clients:merge.merging') : t('clients:merge.action')}
        </Button>
        {error ? (
          <p role="alert" className="text-destructive text-sm">
            {t('clients:merge.error')}
          </p>
        ) : null}
      </div>

      {keeperId !== null ? (
        <MergeConfirm
          open={confirming}
          fromNames={losers.map(nameOf)}
          intoName={nameOf(keeperId)}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void runMerge();
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * The last stop before a merge. Names the record(s) that go away and the one they
 * go into, because "Merge" alone does not say which way it runs, and the loser is
 * deleted for good.
 */
function MergeConfirm({
  open,
  fromNames,
  intoName,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  fromNames: string[];
  intoName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const from = new Intl.ListFormat(i18n.language || 'en', {
    style: 'long',
    type: 'conjunction',
  }).format(fromNames);
  return (
    <ConfirmDialog
      open={open}
      heading={t('clients:merge.confirmHeading')}
      body={t('clients:merge.confirmBody', { count: fromNames.length, from, into: intoName })}
      confirmLabel={t('clients:merge.action')}
      cancelLabel={t('clients:merge.confirmCancel')}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

/** One side of a merge group — the client's fields, plus its "Keep this one" radio. */
function MergeMember({
  id,
  fallbackName,
  keeperId,
  groupName,
  onChoose,
}: {
  id: number;
  fallbackName: string;
  keeperId: number | null;
  groupName: string;
  onChoose: () => void;
}) {
  const { t } = useTranslation();
  const query = useClientQuery(id);

  // The transient states hold the same six rows, so a slow sibling cannot make
  // the grid jump while it loads.
  if (query.isPending) {
    return (
      <div className="min-w-0 rounded-md border border-border bg-background p-4 md:row-span-6">
        <SkeletonRows rows={2} columns={1} label={t('common:status.loading')} />
      </div>
    );
  }
  if (query.isError || !query.data.customer) {
    return (
      <div className="min-w-0 rounded-md border border-border bg-background p-4 md:row-span-6">
        <p className="text-destructive text-sm">{t('clients:merge.memberError', { id })}</p>
      </div>
    );
  }

  const client = query.data.customer;
  const isKeeper = keeperId === id;
  // Five radios all named "Keep this one" sound identical to a screen reader.
  // The name stays (it names the ACTION); the client names the record it acts on.
  const nameId = `merge-member-${id}`;

  return (
    <div
      className={cn(
        'min-w-0 rounded-md border p-4 md:row-span-6 md:grid md:grid-rows-subgrid',
        isKeeper ? 'border-primary bg-primary/5' : 'border-border bg-background',
      )}
    >
      <p id={nameId} className="font-semibold text-foreground">
        {client.name || fallbackName}
      </p>
      <dl className="mt-2 min-w-0 space-y-1 text-sm md:row-span-4 md:mt-0 md:grid md:grid-rows-subgrid md:space-y-0">
        <MemberField label={t('clients:detail.email')} value={client.email} />
        <MemberField label={t('clients:detail.phone')} value={client.phone} />
        <MemberField label={t('clients:detail.address')} value={client.address} />
        <MemberField
          label={t('clients:table.documents')}
          value={<span className="tabular-nums">{client.document_count}</span>}
        />
      </dl>
      {/* The choice is the last thing in the card because you read the record
          before you decide, and it is anchored to the shared bottom row. */}
      <label className="mt-3 flex min-h-11 items-center gap-2 font-semibold text-foreground text-sm md:mt-0">
        <input
          type="radio"
          name={groupName}
          checked={isKeeper}
          onChange={onChoose}
          aria-describedby={nameId}
          className={RADIO}
        />
        {t('clients:merge.keepThisOne')}
      </label>
    </div>
  );
}

function MemberField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <dt className={cn('w-20 shrink-0', FIELD_LABEL)}>{label}</dt>
      <dd className="min-w-0 flex-1 [overflow-wrap:anywhere] text-foreground text-sm">
        {value ?? '—'}
      </dd>
    </div>
  );
}

/**
 * The no-candidates fallback: pick two clients by search. Both sides filter the
 * same loaded directory (the list is small and already fetched — the employees
 * screen's in-memory filter rationale); a side only enables Merge once a KEEPER
 * is chosen, because a merge without an explicit direction is a coin flip.
 *
 * It carries its own three states. Before this it had none: while the directory
 * loaded, and again when the request FAILED, both dropdowns rendered with nothing
 * but their placeholder — so a broken connection and a tenant with no clients were
 * the same picture, and neither said anything. A tenant with fewer than two clients
 * gets its own answer too, because no amount of searching will ever fill the second
 * dropdown.
 */
function ManualPicker() {
  const { t } = useTranslation();
  const list = useClientsQuery({});
  const merge = useMergeClientMutation();

  const [textA, setTextA] = useState('');
  const [textB, setTextB] = useState('');
  const [idA, setIdA] = useState<number | null>(null);
  const [idB, setIdB] = useState<number | null>(null);
  const [keeperId, setKeeperId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  const all = list.data?.items ?? [];
  const matchesA = matches(all, textA, [idB]);
  const matchesB = matches(all, textB, [idA]);

  function side(slot: 'a' | 'b') {
    const text = slot === 'a' ? textA : textB;
    const setText = slot === 'a' ? setTextA : setTextB;
    const id = slot === 'a' ? idA : idB;
    const setId = slot === 'a' ? setIdA : setIdB;
    const options = slot === 'a' ? matchesA : matchesB;
    return { text, setText, id, setId, options };
  }

  const loserId = keeperId === idA ? idB : keeperId === idB ? idA : null;

  const nameOf = (id: number) =>
    all.find((client) => client.id === id)?.name || t('clients:table.unnamedClient', { id });

  async function runMerge() {
    if (idA === null || idB === null || keeperId === null || loserId === null) return;
    setError(false);
    try {
      await merge.mutateAsync({ loserId, intoId: keeperId });
      setDone(true);
      setIdA(null);
      setIdB(null);
      setKeeperId(null);
      setTextA('');
      setTextB('');
    } catch {
      setError(true);
    }
  }

  if (list.isPending) {
    return (
      <section className={`${SECTION} max-w-3xl`}>
        <SkeletonRows rows={4} columns={2} label={t('clients:merge.pickHeading')} />
      </section>
    );
  }

  if (list.isError) {
    return (
      <section className={`${SECTION} max-w-3xl`} role="alert" aria-live="assertive">
        <h2 className="font-display font-semibold text-foreground text-lg">
          {t('clients:error.heading')}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm">{t('clients:error.body')}</p>
        <div className="mt-6">
          <Button type="button" variant="secondary" onClick={() => void list.refetch()}>
            {t('clients:error.retry')}
          </Button>
        </div>
      </section>
    );
  }

  if (all.length < 2) {
    return (
      <section className={`${SECTION} max-w-3xl`}>
        <h2 className="font-display font-semibold text-foreground text-lg">
          {t('clients:merge.tooFewHeading')}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm">{t('clients:merge.tooFewBody')}</p>
      </section>
    );
  }

  return (
    <section className={`${SECTION} max-w-3xl`}>
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('clients:merge.pickHeading')}
      </h2>
      <p className="mb-4 text-muted-foreground text-sm">{t('clients:merge.pickBody')}</p>

      <div className="grid gap-4 md:grid-cols-2">
        <fieldset className="space-y-2">
          <legend className="font-semibold text-foreground text-sm">
            {t('clients:merge.sideA')}
          </legend>
          <SearchField
            id="merge-search-a"
            value={textA}
            onChange={setTextA}
            placeholder={t('clients:merge.searchPlaceholder')}
            label={t('clients:merge.searchLabelA')}
          />
          <ClientSelect
            id="merge-select-a"
            label={t('clients:merge.selectLabelA')}
            options={side('a').options}
            value={idA}
            onChange={setIdA}
          />
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="font-semibold text-foreground text-sm">
            {t('clients:merge.sideB')}
          </legend>
          <SearchField
            id="merge-search-b"
            value={textB}
            onChange={setTextB}
            placeholder={t('clients:merge.searchPlaceholder')}
            label={t('clients:merge.searchLabelB')}
          />
          <ClientSelect
            id="merge-select-b"
            label={t('clients:merge.selectLabelB')}
            options={side('b').options}
            value={idB}
            onChange={setIdB}
          />
        </fieldset>
      </div>

      {/* The choice appears once there is something to choose BETWEEN. Rendering it
          early meant two disabled radios that Chrome draws as filled dots — they read
          as a decision already made, which on this screen is a decision about which
          record gets deleted. The panel's own body copy names the order: pick two,
          then choose which one to keep. */}
      {idA !== null && idB !== null ? (
        <fieldset className="mt-4">
          <legend className="font-semibold text-foreground text-sm">
            {t('clients:merge.keepHeading')}
          </legend>
          <div className="mt-2 flex flex-wrap gap-4">
            <label className="flex min-h-11 items-center gap-2 text-foreground text-sm">
              <input
                type="radio"
                name="keep-picked"
                checked={keeperId === idA}
                onChange={() => setKeeperId(idA)}
                className={RADIO}
              />
              {t('clients:merge.keepA')}
            </label>
            <label className="flex min-h-11 items-center gap-2 text-foreground text-sm">
              <input
                type="radio"
                name="keep-picked"
                checked={keeperId === idB}
                onChange={() => setKeeperId(idB)}
                className={RADIO}
              />
              {t('clients:merge.keepB')}
            </label>
          </div>
        </fieldset>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => {
            setDone(false);
            setConfirming(true);
          }}
          disabled={idA === null || idB === null || keeperId === null || merge.isPending}
        >
          {merge.isPending ? t('clients:merge.merging') : t('clients:merge.action')}
        </Button>
        {done ? (
          <p role="status" className="text-foreground text-sm">
            {t('clients:merge.done')}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-destructive text-sm">
            {t('clients:merge.error')}
          </p>
        ) : null}
      </div>

      {keeperId !== null && loserId !== null ? (
        <MergeConfirm
          open={confirming}
          fromNames={[nameOf(loserId)]}
          intoName={nameOf(keeperId)}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void runMerge();
          }}
        />
      ) : null}
    </section>
  );
}

/** Case-insensitive substring match on the client name, excluding the other side's pick. */
function matches(
  clients: Array<{ id: number; name: string }>,
  text: string,
  excludeIds: Array<number | null>,
): Array<{ id: number; name: string }> {
  const q = text.trim().toLowerCase();
  return clients.filter(
    (client) => !excludeIds.includes(client.id) && (!q || client.name.toLowerCase().includes(q)),
  );
}

function SearchField({
  id,
  value,
  onChange,
  placeholder,
  label,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Input
        id={id}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </>
  );
}

function ClientSelect({
  id,
  label,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  options: Array<{ id: number; name: string }>;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Select
        id={id}
        value={value === null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
      >
        <option value="">{t('clients:merge.pickPlaceholder')}</option>
        {options.map((client) => (
          <option key={client.id} value={String(client.id)}>
            {client.name || t('clients:table.unnamedClient', { id: client.id })}
          </option>
        ))}
      </Select>
    </>
  );
}
